import { useState, useCallback, useEffect } from 'react';

/**
 * Hook to manage trip story sort order with localStorage persistence.
 * Platform-agnostic: Can be adapted for React Native with AsyncStorage.
 *
 * @param tripId - The ID of the trip
 * @param defaultSortOrder - The default sort order from the trip settings
 * @returns Current sort order and toggle function
 */
export const useTripSortOrder = (tripId: string | undefined, defaultSortOrder: 'ASC' | 'DESC') => {
  const [currentSortOrder, setCurrentSortOrder] = useState<'ASC' | 'DESC'>(defaultSortOrder);

  // Load saved preference from localStorage on mount
  useEffect(() => {
    if (!tripId) return;

    const storageKey = `trip-sort-${tripId}`;
    const savedPreference = localStorage.getItem(storageKey);

    if (savedPreference === 'ASC' || savedPreference === 'DESC') {
      setCurrentSortOrder(savedPreference);
    } else {
      // No saved preference, use default
      setCurrentSortOrder(defaultSortOrder);
    }
  }, [tripId, defaultSortOrder]);

  // Toggle between ASC and DESC
  const toggleSortOrder = useCallback(() => {
    if (!tripId) return;

    const newSortOrder = currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
    setCurrentSortOrder(newSortOrder);

    // Save to localStorage
    const storageKey = `trip-sort-${tripId}`;
    localStorage.setItem(storageKey, newSortOrder);
  }, [tripId, currentSortOrder]);

  return {
    currentSortOrder,
    toggleSortOrder,
  };
};
